'use client';

import React, { useRef, useState } from 'react';
import cn from 'classnames';
import Image from '@framework/router/Next/Image';
import Slider, { SliderOptions, SliderRef } from '@dealroadshow/uikit-slider';
import { IClassNames, ITab } from '@framework/ui/Pages/Landing/Sections/TabsSlider/interface';

import styles from './tabsSlider.module.scss';

interface IProps {
  content: ITab[],
  classNames?: IClassNames,
  descriptionAboveDots?: React.ReactNode,
  isReversedPosition?: boolean,
  classNamesDots?: string,
}

const sliderOptions: SliderOptions = {
  slidesPerView: 1,
  speed: 500,
  effect: 'fade',
  fadeEffect: { crossFade: true },
  loop: true,
  autoplay: true,
  pagination: false,
};

const TabsSlider = ({
  content,
  classNames = {},
  descriptionAboveDots,
  isReversedPosition = false,
  classNamesDots,
}: IProps) => {
  const sliderInstanceRef = useRef<SliderRef>(null);
  const [activePaginationIndex, setActivePaginationIndex] = useState<number>(0);

  const handlePaginationClick = (index: number) => {
    sliderInstanceRef.current?.slideToLoop(index);
  };

  return (
    <div
      className={ cn(styles.slidersWrapper, classNames.slidersWrapper, {
        [styles.slidersWrapperIsReversed]: isReversedPosition,
        [styles.slidersWrapperWithDotsDescription]: !!descriptionAboveDots,
      }) }
    >
      <div className={ cn(styles.dotsList, classNames.dotsListClassName) }>
        { descriptionAboveDots }
        <ul className={ styles.dotsWrapper }>
          { content.map((tab, i) => {
            const {
              key,
              icon: Icon,
              title,
              description,
            } = tab ?? {};
            return (
              <li
                key={ `dot${ key }` }
                data-test={ `${ key }SlideButton` }
                className={ cn(styles.dot, { [styles.swiperPaginationBulletActive]: activePaginationIndex === i }) }
                onClick={ () => handlePaginationClick(i) }
              >
                <div className={ cn(styles.dotButton, classNamesDots) }>
                  <span className={ cn(styles.dotNumber, { [styles.iconWrapper]: !!Icon }) }>
                    { Icon ? <Icon /> : i + 1 }
                  </span>
                  <div className={ styles.dotText }>
                    <h4
                      data-test={ `${ key }SlideTitle` }
                      className={ styles.title }
                    >
                      { title }
                    </h4>
                    <div
                      data-test={ `${ key }SlideDescription` }
                      className={ styles.description }
                    >
                      { description }
                    </div>
                  </div>
                </div>
              </li>
            );
          }) }
        </ul>
      </div>

      <Slider
        // @ts-ignore
        instanceRef={ sliderInstanceRef }
        options={ sliderOptions }
        collection={ content }
        className={ styles.slider }
        getItemKey={ (slide) => slide.key }
        renderItem={ (slide) => (
          <div
            key={ slide.key }
            className={ styles.slideContainer }
            data-test={ `${ slide.key }SlideContent` }
          >
            <div>
              <Image
                className={ cn(styles.img, classNames.imgClassName) }
                src={ slide.img.src }
                width={ slide.img.width }
                height={ slide.img.height }
                placeholder="empty"
                alt="Tab image"
              />
              { slide.imgLabel && (
                <p className={ cn(styles.imgLabel, classNames.imgLabelClassName) }>{ slide.imgLabel }</p>
              ) }
            </div>
            <div className={ styles.slideText }>
              <h4 className={ cn(styles.title, classNames.titleClassName) }>
                { slide.title }
              </h4>
              <div className={ styles.description }>{ slide.description }</div>
            </div>
          </div>
        ) }
        onSlideChange={ ({ realIndex }) => setActivePaginationIndex(realIndex) }
      />
    </div>
  );
};

export default TabsSlider;
